/**
 * UC-02: PlaceBuilding
 * TOGAF Application ABB — build / upgrade / delete on grid without UI deps.
 */
import { BUILDINGS } from '../../../constants';
import { BuildingType } from '../../../types';
import type { Grid } from '../../../types';
import type { IAppLogger } from '../../domain/ports/IAppLogger';
import { SimulationEngine } from '../../domain/services/SimulationEngine';
import type {
  PlaceBuildingInput,
  PlaceBuildingOutput,
  PlaceBuildingPreview,
} from '../dto/PlaceBuildingDto';
import type { CalculateConnectivityUseCase } from './CalculateConnectivityUseCase';

export interface PlaceBuildingDependencies {
  simulation: SimulationEngine;
  calculateConnectivity: CalculateConnectivityUseCase;
  logger?: IAppLogger;
}

const MAX_BUILDING_LEVEL = 5;

export class PlaceBuildingUseCase {
  constructor(private readonly deps: PlaceBuildingDependencies) {}

  computePreview(input: PlaceBuildingInput): PlaceBuildingPreview {
    const { grid, stats, action, x, y, buildingType, hasJumpGate } = input;
    const tile = grid[y]?.[x];

    if (!tile) {
      return this.invalidPreview('Tile out of bounds');
    }

    switch (action) {
      case 'place':
        return this.previewPlace(grid, stats, x, y, buildingType);
      case 'upgrade':
        return this.previewUpgrade(grid, stats, x, y, hasJumpGate ?? false);
      case 'delete':
        return this.previewDelete(tile);
      default:
        return this.invalidPreview(`Unknown action: ${action}`);
    }
  }

  executePlace(
    input: Omit<PlaceBuildingInput, 'action' | 'hasJumpGate'> & { buildingType: BuildingType },
  ): PlaceBuildingOutput {
    return this.execute({ ...input, action: 'place' });
  }

  executeUpgrade(
    input: Omit<PlaceBuildingInput, 'action' | 'buildingType'> & { hasJumpGate: boolean },
  ): PlaceBuildingOutput {
    return this.execute({ ...input, action: 'upgrade' });
  }

  executeDelete(
    input: Omit<PlaceBuildingInput, 'action' | 'buildingType' | 'hasJumpGate'>,
  ): PlaceBuildingOutput {
    return this.execute({ ...input, action: 'delete' });
  }

  execute(input: PlaceBuildingInput): PlaceBuildingOutput {
    const preview = this.computePreview(input);

    if (!preview.valid) {
      return {
        ok: false,
        grid: input.grid,
        stats: input.stats,
        budgetDelta: 0,
        connectivityRefreshed: false,
        error: preview.error,
      };
    }

    const newGrid = input.grid.map((row) => row.map((t) => ({ ...t })));
    const newStats = { ...input.stats };
    const tile = newGrid[input.y][input.x];

    switch (input.action) {
      case 'place':
        tile.buildingType = input.buildingType!;
        tile.level = 1;
        tile.isConnected = false;
        break;
      case 'upgrade':
        tile.level = (tile.level || 1) + 1;
        break;
      case 'delete':
        tile.buildingType = BuildingType.None;
        tile.level = 1;
        tile.isConnected = false;
        break;
    }

    const connectedGrid = this.deps.calculateConnectivity.execute({ grid: newGrid }).grid;
    newStats.budget += preview.budgetDelta;

    this.deps.logger?.log('debug', 'building.placed', {
      action: input.action,
      x: input.x,
      y: input.y,
      budgetDelta: preview.budgetDelta,
      willConnect: preview.willConnect,
    });

    return {
      ok: true,
      grid: connectedGrid,
      stats: newStats,
      budgetDelta: preview.budgetDelta,
      connectivityRefreshed: true,
    };
  }

  private previewPlace(
    grid: Grid,
    stats: { budget: number },
    x: number,
    y: number,
    buildingType?: BuildingType,
  ): PlaceBuildingPreview {
    const tile = grid[y][x];

    if (tile.buildingType !== BuildingType.None) {
      return this.invalidPreview('Tile is not empty');
    }

    if (!buildingType || buildingType === BuildingType.None) {
      return this.invalidPreview('No building type selected');
    }

    const config = BUILDINGS[buildingType];
    const cost = config.cost;

    if (stats.budget < cost) {
      return this.invalidPreview('Insufficient funds');
    }

    const willConnect = this.wouldConnectAfterPlace(grid, x, y, buildingType);

    return {
      valid: true,
      cost,
      budgetDelta: -cost,
      complianceDelta: config.complianceGen,
      threatDelta: config.threatGen,
      willConnect,
    };
  }

  private previewUpgrade(
    grid: Grid,
    stats: { budget: number },
    x: number,
    y: number,
    hasJumpGate: boolean,
  ): PlaceBuildingPreview {
    if (!hasJumpGate) {
      return this.invalidPreview('ACCESS DENIED: Upgrades require Jump Gate.');
    }

    const tile = grid[y][x];

    if (tile.buildingType === BuildingType.None) {
      return this.invalidPreview('No building to upgrade');
    }

    const level = tile.level || 1;
    if (level >= MAX_BUILDING_LEVEL) {
      return this.invalidPreview('Building is at max level');
    }

    const config = BUILDINGS[tile.buildingType];
    const cost = Math.floor(config.cost * level * 1.2);

    if (stats.budget < cost) {
      return this.invalidPreview('Insufficient funds for upgrade');
    }

    return {
      valid: true,
      cost,
      budgetDelta: -cost,
      complianceDelta: config.complianceGen,
      threatDelta: config.threatGen,
      willConnect: tile.isConnected ?? false,
    };
  }

  private previewDelete(tile: { buildingType: BuildingType }): PlaceBuildingPreview {
    if (tile.buildingType === BuildingType.None) {
      return this.invalidPreview('No building to delete');
    }

    if (tile.buildingType === BuildingType.Core) {
      return this.invalidPreview('Cannot dismantle The HUB.');
    }

    return {
      valid: true,
      cost: 0,
      budgetDelta: 0,
      complianceDelta: 0,
      threatDelta: 0,
      willConnect: false,
    };
  }

  private wouldConnectAfterPlace(
    grid: Grid,
    x: number,
    y: number,
    buildingType: BuildingType,
  ): boolean {
    const draft = grid.map((row) => row.map((t) => ({ ...t })));
    draft[y][x] = { ...draft[y][x], buildingType, level: 1, isConnected: false };
    const connected = this.deps.calculateConnectivity.execute({ grid: draft }).grid;
    return connected[y][x].isConnected === true;
  }

  private invalidPreview(error: string): PlaceBuildingPreview {
    return {
      valid: false,
      cost: 0,
      budgetDelta: 0,
      complianceDelta: 0,
      threatDelta: 0,
      willConnect: false,
      error,
    };
  }
}